import { equal } from '@/api/helpers';
import { Account } from '@/client/accounts';
import { Branch } from '@/client/branches';
import { SendTestEmailFormValues } from '@/client/campaigns';
import { CourseEntityEnrollEnum } from '@/client/courses';
import { Group } from '@/client/groups';
import { SecurityCultureSendSurveyFormValues } from '@/client/security-culture/types';
import { Subjects } from '@/client/users';
import { LoadingStatuses } from '@/common/constants';
import { TargetEntity, TargetFormValue } from '@/common/types/targets.type';
import {
  FormikDropdown,
  FormikInput,
  FormikMultiSelect,
} from '@/components/form';
import {
  useBranchesPartialRequest,
  useGroupsPartialRequest,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { AppCalendarWithCustomTimepicker } from '@/ui/calendar/AppCalendarWithCustomTimepicker';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import { formatTargetEntities } from '@/utils/targets';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import { DropdownChangeEvent } from 'primereact/dropdown';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SendTestEmailModal } from '../campaigns';
import { securityCultureSchema } from './validations';

type SecurityCultureSendSurveyFormProps = {
  onSubmit: (
    data: SecurityCultureSendSurveyFormValues,
    resetForm: () => void,
    isTypeForm?: boolean,
  ) => void;
  onSendTestEmail: (
    data: SecurityCultureSendSurveyFormValues,
    test: SendTestEmailFormValues,
  ) => void;
  account: Account;
  loading: LoadingStatuses;
};

export const SecurityCultureSendSurveyForm: React.FC<
  SecurityCultureSendSurveyFormProps
> = ({ onSubmit, onSendTestEmail, loading }) => {
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const [testEmail, setTestEmail] = useState(false);
  const [initialValues] = useState<SecurityCultureSendSurveyFormValues>({
    target: undefined,
    targets: undefined,
    typeFormId: '',
    scheduledFor: undefined,
  });

  const groupedEntities = useRef<
    {
      label: string;
      items: TargetEntity[];
    }[]
  >([]);

  const { branches, isLoading: isBranchesLoading } = useBranchesPartialRequest({
    accountId: currentAccount?.id,
    filters: [equal('active', true)],
    sort: ['name,asc'],
    withDescendants: true,
    take: 20,
  });

  const { groups, isLoading: isGroupsLoading } = useGroupsPartialRequest({
    accountId: currentAccount?.id,
    filters: [equal('active', true)],
    sort: ['name,asc'],
  });

  useEffect(() => {
    if (currentAccount && !isBranchesLoading && !isGroupsLoading) {
      const entities = [];

      entities.push({
        label: t('account'),
        items: [currentAccount],
      });

      if (branches?.length) {
        entities.push({
          label: t('branches'),
          items: formatTargetEntities(
            branches,
            null,
            CourseEntityEnrollEnum.BRANCHES,
          ),
        });
      }

      if (groups?.length) {
        entities.push({
          label: t('groups'),
          items: formatTargetEntities(
            groups,
            null,
            CourseEntityEnrollEnum.GROUPS,
          ),
        });
      }

      groupedEntities.current = entities;
    }
  }, [isBranchesLoading, isGroupsLoading]);

  const checkIsOptionDisabled = (
    targets: TargetFormValue[],
    option: TargetFormValue,
  ) => {
    if (
      targets.find(
        (entity: TargetFormValue) =>
          entity.id === currentAccount?.id && option.id !== currentAccount?.id,
      ) ||
      targets
        .filter((target: TargetEntity) => !!target?.descendants)
        .find((target: TargetEntity) =>
          target.descendants?.find((x: any) => x.id === option.id),
        )
    )
      return true;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={securityCultureSchema(
        t,
        canUseFeature(Subjects.SECURITY_CULTURE_TYPEFORM_INTEGRATION),
        true,
      )}
      validateOnMount
      onSubmit={(data, { resetForm }) =>
        onSubmit(
          data,
          resetForm,
          canUseFeature(Subjects.SECURITY_CULTURE_TYPEFORM_INTEGRATION),
        )
      }
    >
      {({ setFieldValue, values, errors }) => {
        return (
          <FormContainer>
            <Form className="w-full">
              {canUseFeature(Subjects.SECURITY_CULTURE_TYPEFORM_INTEGRATION) ? (
                <div className="field w-full">
                  <Field
                    id="target"
                    name="target"
                    dropdownIcon={
                      isBranchesLoading || isGroupsLoading
                        ? 'pi pi-spinner pi-spin'
                        : 'pi pi-chevron-down'
                    }
                    filter
                    label={t('generic.recipients')}
                    className="w-full"
                    placeholder={t('generic.select')}
                    required
                    onChange={(event: DropdownChangeEvent) =>
                      setFieldValue('target', event.value)
                    }
                    dataKey="id"
                    optionLabel="name"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    component={FormikDropdown}
                    options={groupedEntities.current}
                    disabled={
                      isBranchesLoading ||
                      isGroupsLoading ||
                      loading === LoadingStatuses.LOADING
                    }
                    tooltip={t('security.culture.branches.tooltip')}
                  />
                </div>
              ) : (
                <div className="field w-full">
                  <Field
                    id="targets"
                    name="targets"
                    dropdownIcon={
                      isBranchesLoading || isGroupsLoading
                        ? 'pi pi-spinner pi-spin'
                        : 'pi pi-chevron-down'
                    }
                    filter
                    label={t('generic.recipients')}
                    className="w-full"
                    display="chip"
                    placeholder={t('generic.select')}
                    required
                    optionDisabled={(option: Account | Branch | Group) =>
                      checkIsOptionDisabled(values.targets || [], option)
                    }
                    onChange={(event: DropdownChangeEvent) => {
                      const accountSelected = event.value.find(
                        (item: any) => item.id === currentAccount?.id,
                      );

                      // if account is selected, disselect all other entities
                      if (accountSelected) {
                        setFieldValue('targets', [currentAccount]);
                      } else {
                        setFieldValue('targets', event.value);
                      }
                    }}
                    dataKey="id"
                    optionLabel="name"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    component={FormikMultiSelect}
                    options={groupedEntities.current}
                    disabled={
                      isBranchesLoading ||
                      isGroupsLoading ||
                      loading === LoadingStatuses.LOADING
                    }
                    tooltip={t('security.culture.branches.tooltip')}
                  />
                </div>
              )}
              {canUseFeature(
                Subjects.SECURITY_CULTURE_TYPEFORM_INTEGRATION,
              ) && (
                <div className="field w-full">
                  <Field
                    id="typeFormId"
                    name="typeFormId"
                    label={t('security.culture.typeFormId')}
                    className="w-full"
                    component={FormikInput}
                    disabled={loading === LoadingStatuses.LOADING}
                  />
                </div>
              )}
              <div className="field w-full mb-4">
                <label htmlFor="scheduledFor">
                  {t('security.culture.scheduleFor')}
                </label>
                <AppCalendarWithCustomTimepicker
                  id="scheduledFor"
                  name="scheduledFor"
                  className="w-full"
                  minDate={moment.utc().toDate()}
                  value={values.scheduledFor}
                  showTime
                  stepMinute={15}
                  stepHour={1}
                  onChange={(value: Date | null) =>
                    setFieldValue('scheduledFor', value)
                  }
                  readOnlyInput
                  showButtonBar
                  placeholder={t('security.culture.scheduleFor.placeholder')}
                  disabled={loading === LoadingStatuses.LOADING}
                />
              </div>
              <FlexContainer
                justify="space-between"
                className="mt-5"
                wrap="wrap"
                gap={12}
              >
                <AppButton
                  label={t('camapign.testEmail.send')}
                  severity="secondary"
                  type="outlined"
                  onClick={() => setTestEmail(true)}
                  state={loading}
                />
                <AppButton
                  label={t('security.culture.sendSurveyTarget')}
                  isSubmit
                  isDisabled={!!Object.keys(errors).length}
                  state={loading}
                />
              </FlexContainer>

              <SendTestEmailModal
                email={currentUser?.email as string}
                visible={testEmail}
                onSubmit={(test: SendTestEmailFormValues) => {
                  onSendTestEmail(values, test);
                  setTestEmail(false);
                }}
                onHide={() => setTestEmail(false)}
                extended={false}
                withName
                isSecurityCulture
              />
            </Form>
          </FormContainer>
        );
      }}
    </Formik>
  );
};
